import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import MoonAvatar from "@/components/MoonAvatar";
import StarLoader from "@/components/StarLoader";

export default function StyleInspiration() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/style-looks")
      .then(({ data }) => setLooks(data || []))
      .catch(() => setLooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen bg-white" data-testid="style-page">
      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 sm:py-16">
        <div className="text-center mb-10 sm:mb-14">
          <div className="overline">Style inspiration</div>
          <div className="mt-2 flex items-center justify-center gap-4">
            <MoonAvatar size={56} />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-sj-ink">
              Styled by <span className="gold-text font-medium">Moon</span>
            </h1>
          </div>
          <p className="mt-4 text-sj-inkSoft italic max-w-xl mx-auto text-base">
            Curated styling ideas by Moon.
          </p>
        </div>

        {loading ? (
          <StarLoader full label="Loading looks" />
        ) : looks.length === 0 ? (
          <div className="text-center text-sj-muted text-sm py-16">No looks yet. Check back soon.</div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-5 [column-fill:_balance]">
            {looks.map((s, i) => {
              const primaryPid = (s.product_ids && s.product_ids[0]) || null;
              const href = primaryPid ? `/product/${primaryPid}` : "#";
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: (i % 4) * 0.05 }}
                  className="mb-3 sm:mb-5 break-inside-avoid"
                  data-testid={`style-look-full-${s.id}`}
                >
                  <Link
                    to={href}
                    className="relative block overflow-hidden rounded-lg group shadow-sm hover:shadow-xl transition-shadow duration-500"
                    aria-disabled={!primaryPid}
                    onClick={(e) => { if (!primaryPid) e.preventDefault(); }}
                  >
                    {s.image ? (
                      <img
                        src={resolveImage(s.image, { w: 900 })}
                        alt={s.label}
                        className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-sj-cream flex items-center justify-center text-sj-muted text-xs">No image</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                    <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 flex items-center justify-between opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
                      <span className="text-white text-[11px] sm:text-xs tracking-wide">{s.label}</span>
                      {primaryPid && (
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium bg-white/95 text-sj-ink px-2.5 py-1.5 rounded-full">
                          Shop This Look <ArrowRight size={11} />
                        </span>
                      )}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
